import BranchRequiredWrapper from "@/components/banners/BranchRequiredWrapper";
import WarningBanner from "@/components/banners/WarningBanner";
import BranchOperatingHoursInput from "@/components/selectors/BranchOperatingHoursInput";
import { useUser } from "@/contexts/UserContext";
import { useAxiosInstance } from "@/utils/useAxiosInstance";
import { faImage, faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import FeatureManagement from "./FeatureManagement";
import Spinner from "@/components/loader/Spinner";
import WarningPopup, { WarningPopupProps } from "../common/warning-popup";
import { t } from "i18next";
import { Button } from "@/components/ui/button";
import { PencilIcon } from "lucide-react";
import { Branch } from "@/utils/types";
import EditBranchInformation from "./EditBranchInformation";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface OperatingHour {
  id: number;
  day: string;
  open_time: string;
  close_time: string;
  branch: number;
}

const BusinessInformation = () => {
  const { user, login } = useUser();
  const [axiosInstance, loading, isFetching] = useAxiosInstance();
  const navigate = useNavigate();
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [isError, setIsError] = useState(false);
  const [animateOut, setAnimateOut] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState<Branch>({} as Branch);
  const [operatingHours, setOperatingHours] = useState<OperatingHour[]>([]);
  const [logo, setLogo] = useState<string | null>(null);
  const [isLogoUploaded, setIsLogoUploaded] = useState(false);
  const [promotionText, setPromotionText] = useState<string>("");
  const [logoPreviewUrl, setLogoPreviewUrl] = useState("");
  const [featureState, setFeatureState] = useState({
    delivery_feature: false,
    upsell_feature: false,
    loyalty_program_enabled: false,
    is_store_open: true,
    duplicate_receipts: false,
  });
  const [branchPreference, setBranchPreference] = useState({
    menu_preference: "",
  });
  const [paymentPreference, setPaymentPreference] = useState({
    payment_preference: "",
  });
  const [imgExtension, setImgExtension] = useState("");
  const [showWarningPopup, setShowWarningPopup] = useState(false);
  const [warningPopUp, setWarningPopUp] = useState<WarningPopupProps>({
    title: "",
    description: "",
    onCancel: () => {},
    onConfirm: () => {},
  });
  const [isEditBranchInformation, setIsEditBranchInformation] = useState(false);
  const [appointmentBufferTime, setAppointmentBufferTime] = useState(0);
  const [tipOptions, setTipOptions] = useState({
    tip_1: 0,
    tip_2: 0,
    tip_3: 0,
  });
  const [taxRate, setTaxRate] = useState(0);
  const [businessType, setBusinessType] = useState<string>("");

  const showWarningPopupHandler = ({
    title,
    description,
    onCancel,
    onConfirm,
  }: {
    title: string;
    description: string;
    onCancel: () => void;
    onConfirm: () => void;
  }) => {
    setWarningPopUp({ title, description, onCancel, onConfirm });
    setShowWarningPopup(true);
  };

  // Fetch branches by business name
  const fetchBranchesByBusinessName = useCallback(
    async (businessName: string) => {
      if (loading || !axiosInstance) return;
      try {
        const targetBranchId = user?.selectedBranch?.id;
        const response = await axiosInstance.get(
          `api/v1/branch/${targetBranchId}/?business_name=${businessName}`,
        );
        const curBranch = response.data;
        console.log("Current branch:", curBranch);

        if (curBranch) {
          const updates = {
            selectedBranch: curBranch,
            featureState: {
              is_active: curBranch.is_active,
              delivery_feature: curBranch.delivery_feature || false,
              upsell_feature: curBranch.upsell_feature || false,
              loyalty_program_enabled:
                curBranch.loyalty_program_enabled || false,
              is_store_open: curBranch.is_store_open || false,
              duplicate_receipts: curBranch.duplicate_receipts || false,
            },
            operatingHours: curBranch.operating_hours,
          };

          const storedUserData = localStorage.getItem("user_data");
          if (storedUserData) {
            const userData = JSON.parse(storedUserData);
            userData.selectedBranch = curBranch;
            localStorage.setItem("user_data", JSON.stringify(userData));
          }

          setSelectedBranch(updates.selectedBranch);
          setFeatureState(updates.featureState);
          setBranchPreference({
            menu_preference: updates.selectedBranch.menu_preference,
          });
          setPaymentPreference({
            payment_preference: updates.selectedBranch.payment_preference,
          });
          setAppointmentBufferTime(
            updates.selectedBranch.appointment_buffer_time || 0,
          );
          setOperatingHours(updates.operatingHours);
          setTipOptions({
            tip_1: updates.selectedBranch.tip_1 || 0,
            tip_2: updates.selectedBranch.tip_2 || 0,
            tip_3: updates.selectedBranch.tip_3 || 0,
          });
          setTaxRate(updates.selectedBranch.tax_rate || 0);
          setBusinessType(updates.selectedBranch.business_type || "");
        }
      } catch (error) {
        console.error("Error fetching branches:", error);
      }
    },
    [axiosInstance, loading, user?.selectedBranch?.id],
  );

  useEffect(() => {
    const storedUserData = localStorage.getItem("user_data");
    const userData = storedUserData ? JSON.parse(storedUserData) : null;
    const businessName = userData?.owner?.business_name;

    if (businessName && user?.selectedBranch?.id) {
      fetchBranchesByBusinessName(businessName);
    }
  }, [fetchBranchesByBusinessName, user?.selectedBranch?.id]);

  useEffect(() => {
    const storedUserData = localStorage.getItem("user_data");
    const userData = storedUserData ? JSON.parse(storedUserData) : null;

    if (userData?.selectedBranch) {
      setPromotionText(userData.selectedBranch.promotion_text || "");
      if (userData.selectedBranch.logo) {
        setLogoPreviewUrl(userData.selectedBranch.logo);
        setIsLogoUploaded(true);
      }
    }
  }, []);

  useEffect(() => {
    if (showBanner) {
      setAnimateOut(false);
      const timerId = setTimeout(() => {
        setAnimateOut(true);
        setTimeout(() => setShowBanner(false), 500);
      }, 3000);
      return () => clearTimeout(timerId);
    }
  }, [showBanner]);

  const handleChange = async (name: string, value: boolean) => {
    if (loading || !axiosInstance) return;

    try {
      const apiUrl = `${import.meta.env.VITE_APP_API_BASE_URL}api/v1/branch/${
        selectedBranch.id
      }/?business_name=${user?.owner?.business_name}`;

      const payload = {
        ...featureState,
        [name]: value,
        user: user?.id,
        name: selectedBranch?.name,
        phone: selectedBranch?.phone,
        email: selectedBranch?.email,
        delivery_cost: selectedBranch?.delivery_cost,
        base_distance: selectedBranch?.base_distance,
        delivery_cost_per_additional_mile:
          selectedBranch?.delivery_cost_per_additional_mile,
        max_distance: selectedBranch?.max_distance,
        auto_gratuity_guest_threshold:
          selectedBranch?.auto_gratuity_guest_threshold,
        auto_gratuity_percentage: selectedBranch?.auto_gratuity_percentage,
      };

      await axiosInstance.put(apiUrl, payload);

      setFeatureState((p) => ({ ...p, [name]: value }));

      const storedUserData = localStorage.getItem("user_data");
      const userData = storedUserData ? JSON.parse(storedUserData) : null;
      const businessName = userData?.owner?.business_name;
      if (businessName) {
        fetchBranchesByBusinessName(businessName);
      }
      switch (name) {
        case "delivery_feature":
          if (value) {
            toast.success("Delivery feature enabled successfully.");
          } else {
            toast.success("Delivery feature disabled successfully.");
          }
          break;
        case "upsell_feature":
          if (value) {
            toast.success("Upsell feature enabled successfully.");
          } else {
            toast.success("Upsell feature disabled successfully.");
          }
          break;
        case "loyalty_program_enabled":
          if (value) {
            toast.success("Loyalty program enabled successfully.");
          } else {
            toast.success("Loyalty program disabled successfully.");
          }
          break;
        case "is_store_open":
          if (value) {
            toast.success("Store open status updated successfully.");
          } else {
            toast.success("Store closed status updated successfully.");
          }
          break;
        case "is_active":
          if (value) {
            toast.success("Branch reactivated successfully.");
          } else {
            toast.success("Branch deactivated successfully.");
          }
          break;
        case "auto_gratuity":
          toast.success("Auto gratuity settings updated successfully.");
          break;
        case "duplicate_receipts":
          if (value) {
            toast.success("Duplicate receipts enabled successfully.");
          } else {
            toast.success("Duplicate receipts disabled successfully.");
          }
          break;
        default:
          toast.success(`${name} updated successfully.`);
      }
    } catch (error) {
      console.error("Error updating feature:", error);
      toast.error("Error updating settings. Please try again.");
    }
  };

  const handleDeleteBranch = async () => {
    if (loading || !axiosInstance) return;
    try {
      const apiUrl = `${import.meta.env.VITE_APP_API_BASE_URL}api/v1/branch/${
        selectedBranch.id
      }/?business_name=${user?.owner?.business_name}`;
      await axiosInstance.delete(apiUrl);

      const storedUserData = localStorage.getItem("user_data");
      const userData = storedUserData ? JSON.parse(storedUserData) : null;
      userData.selectedBranch = null;
      console.log(userData);
      localStorage.setItem("user_data", JSON.stringify(userData));

      setSelectedBranch({} as Branch);
      setFeatureState({
        delivery_feature: false,
        upsell_feature: false,
        loyalty_program_enabled: false,
        is_store_open: true,
        duplicate_receipts: false,
      });
      setOperatingHours([]);

      login(userData);

      toast.success("Branch deleted successfully.");
      navigate("/");
    } catch (error) {
      console.error("Error deleting branch:", error);
      toast.error("Error deleting branch. Please try again.");
    }
  };

  const handlePromotionTextChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setPromotionText(event.target.value);
  };

  const updatePromotionText = async () => {
    if (loading || !axiosInstance) return;

    try {
      const apiUrl = `${import.meta.env.VITE_APP_API_BASE_URL}api/v1/branch/${
        selectedBranch.id
      }/?business_name=${user?.owner?.business_name}`;
      await axiosInstance.put(apiUrl, {
        user: user?.id,
        name: selectedBranch?.name,
        promotion_text: promotionText,
        phone: selectedBranch?.phone,
        email: selectedBranch?.email,
      });

      // Update localStorage after successful API call
      const storedUserData = localStorage.getItem("user_data");
      if (storedUserData) {
        const userData = JSON.parse(storedUserData);
        if (userData.selectedBranch) {
          userData.selectedBranch.promotion_text = promotionText;
          localStorage.setItem("user_data", JSON.stringify(userData));
        }
      }

      toast.success("Promotion text updated successfully.");
    } catch (error) {
      console.error("Error updating Promotion Text:", error);
      toast.error("Error updating Promotion Text. Please try again.");
    }
  };

  const fileToBase64 = (
    file: File,
    callback: (result: string | ArrayBuffer | null) => void,
  ): void => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => callback(reader.result);
    reader.onerror = (error) =>
      console.log("Error converting file to Base64:", error);
  };

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      if (file.size > 20000000) {
        toast.error(
          "File size is too large. Please upload an image less than 20MB.",
        );
        return;
      }
      setImgExtension(file.name.split(".")[1]);
      setLogoPreviewUrl(URL.createObjectURL(file));
      setIsLogoUploaded(true);

      fileToBase64(file, (base64Result: string | ArrayBuffer | null) => {
        if (typeof base64Result === "string") {
          const base64Data = base64Result.split(",")[1];
          setLogo(base64Data);
        }
      });
    }
  };

  const handleRemoveLogo = () => {
    setLogoPreviewUrl("");
    setIsLogoUploaded(false);
    setLogo(null);
  };

  const handleUpdateLogo = async () => {
    if (loading || !axiosInstance) return;

    const apiUrl = `${import.meta.env.VITE_APP_API_BASE_URL}api/v1/branch/${
      selectedBranch.id
    }/?business_name=${user?.owner?.business_name}`;
    if (!apiUrl) {
      console.error("API URL is not defined.");
      setIsError(true);
      setFeedbackMessage("Configuration error. Please try again later.");
      setShowBanner(true);
      return;
    }
    try {
      if (logo && user?.owner) {
        const response = await axiosInstance.put(apiUrl, {
          logo: logo,
          user: user?.id,
          name: selectedBranch?.name,
          phone: selectedBranch?.phone,
          email: selectedBranch?.email,
          logo_extension: imgExtension,
        });

        const storedUserData = localStorage.getItem("user_data");
        if (storedUserData) {
          const userData = JSON.parse(storedUserData);
          if (userData.selectedBranch) {
            userData.selectedBranch.logo = response.data.logo;
            localStorage.setItem("user_data", JSON.stringify(userData));
          }
        }

        toast.success("Logo updated successfully.");
      }
    } catch (error) {
      console.error("Error updating Logo:", error);
      toast.error("Error updating Logo. Please try again.");
    }
  };

  const handleSaveBranchPreference = async () => {
    if (loading || !axiosInstance) return;

    try {
      const apiUrl = `${import.meta.env.VITE_APP_API_BASE_URL}api/v1/branch/${
        selectedBranch.id
      }/?business_name=${user?.owner?.business_name}`;

      await axiosInstance.put(apiUrl, {
        user: user?.id,
        name: selectedBranch?.name,
        phone: selectedBranch?.phone,
        email: selectedBranch?.email,
        menu_preference: branchPreference.menu_preference,
        payment_preference: paymentPreference.payment_preference,
        appointment_buffer_time: appointmentBufferTime,
        tax_rate: taxRate,
        tip_1: tipOptions.tip_1,
        tip_2: tipOptions.tip_2,
        tip_3: tipOptions.tip_3,
      });

      // Update localStorage
      const storedUserData = localStorage.getItem("user_data");
      if (storedUserData) {
        const userData = JSON.parse(storedUserData);
        if (userData.selectedBranch) {
          userData.selectedBranch.menu_preference =
            branchPreference.menu_preference;
          userData.selectedBranch.payment_preference =
            paymentPreference.payment_preference;
          userData.selectedBranch.appointment_buffer_time =
            appointmentBufferTime;
          userData.selectedBranch.tax_rate = taxRate;
          userData.selectedBranch.tip_1 = tipOptions.tip_1;
          userData.selectedBranch.tip_2 = tipOptions.tip_2;
          userData.selectedBranch.tip_3 = tipOptions.tip_3;
          localStorage.setItem("user_data", JSON.stringify(userData));
        }
      }

      toast.success("Branch preferences updated successfully.");
    } catch (error) {
      console.error("Error updating branch preferences:", error);
      toast.error("Error updating branch preferences. Please try again.");
    }
  };

  const handleCancelBranchPreference = () => {
    // Reset to the original preferences from selectedBranch
    setBranchPreference({
      menu_preference: selectedBranch.menu_preference || "Physical Products",
    });
    setPaymentPreference({
      payment_preference: selectedBranch.payment_preference || "PrePaid",
    });
  };

  const handleUpdateBusinessType = async (value: string) => {
    if (loading || !axiosInstance) return;
    
    try {
      const apiUrl = `${import.meta.env.VITE_APP_API_BASE_URL}api/v1/branch/${
        selectedBranch.id
      }/?business_name=${user?.owner?.business_name}`;
      
      await axiosInstance.patch(apiUrl, {
        business_type: value
      });
      
      setBusinessType(value);
      
      // Update localStorage
      const storedUserData = localStorage.getItem("user_data");
      if (storedUserData) {
        const userData = JSON.parse(storedUserData);
        if (userData.selectedBranch) {
          userData.selectedBranch.business_type = value;
          localStorage.setItem("user_data", JSON.stringify(userData));
        }
      }
      
      toast.success("Business type updated successfully.");
    } catch (error) {
      console.error("Error updating business type:", error);
      toast.error("Error updating business type. Please try again.");
    }
  };

  if (isEditBranchInformation) {
    return (
      <EditBranchInformation
        setIsEditBranchInformation={setIsEditBranchInformation}
        initialBranch={selectedBranch}
        setInitialBranch={setSelectedBranch}
      />
    );
  }

  return (
    <BranchRequiredWrapper>
      {isFetching ? (
        <Spinner />
      ) : (
        <div>
          {showBanner && (
            <WarningBanner
              title={isError ? "Error" : "Success"}
              text={feedbackMessage}
              isSuccess={!isError}
              className={`${
                animateOut ? "animate-slideOutRight" : "animate-slideDown"
              }`}
            />
          )}
          {/* Branch's Information Section */}
          <div className="my-4 rounded-lg bg-white p-4 shadow">
            <div className="mb-6 flex flex-col items-start justify-start md:mb-2 md:flex-row md:items-center md:justify-between">
              <h3 className="mb-2 text-lg font-semibold">
                Branch's Information
              </h3>
              <Button onClick={() => setIsEditBranchInformation(true)}>
                <PencilIcon className="mr-2 h-4 w-4" />
                Edit Information
              </Button>
            </div>
            <div className="grid gap-6 sm:grid-cols-1 md:grid-cols-2">
              <div className="flex flex-col gap-2">
                <p className="font-semibold text-gray-700">Business Name:</p>
                <p className="pl-4 text-gray-600">{selectedBranch.name}</p>
              </div>
              <div className="flex flex-col gap-2">
                <p className="font-semibold text-gray-700">Business Phone:</p>
                <p className="pl-4 text-gray-600">
                  {selectedBranch.phone === "" ? "-" : selectedBranch.phone}
                </p>
              </div>
              <div className="flex flex-col gap-2">
                <p className="font-semibold text-gray-700">Owner Phone:</p>
                <p className="pl-4 text-gray-600">
                  {selectedBranch.owner_phone === ""
                    ? "-"
                    : selectedBranch.owner_phone}
                </p>
              </div>
              <div className="flex flex-col gap-2">
                <p className="font-semibold text-gray-700">AI Agent Phone:</p>
                <p className="pl-4 text-gray-600">
                  {selectedBranch.ai_phone === ""
                    ? "-"
                    : selectedBranch.ai_phone}
                </p>
              </div>
              <div className="flex flex-col gap-2">
                <p className="font-semibold text-gray-700">Business Type:</p>
                <div className="pl-4">
                  <Select 
                    value={businessType} 
                    onValueChange={handleUpdateBusinessType}
                  >
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Select business type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Restaurant">Restaurant</SelectItem>
                      <SelectItem value="Services">Services</SelectItem>
                      <SelectItem value="Retail">Retail</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <p className="font-semibold text-gray-700">Business Email:</p>
                <p className="pl-4 text-gray-600">{selectedBranch.email}</p>
              </div>
            </div>
            <div className="mt-4 flex flex-col gap-2">
              <p className="font-semibold text-gray-700">Business Address:</p>
              <p className="pl-4 text-gray-600">
                {selectedBranch.address?.street}, {selectedBranch.address?.city}
                , {selectedBranch.address?.province},{" "}
                {selectedBranch.address?.postal_code}
              </p>
            </div>
          </div>

          <div className="my-4 rounded-lg bg-white p-4 shadow">
            <h3 className="mb-2 text-lg font-semibold">Branch Preference</h3>
            <p className="pb-4">
              You can set preferences for the branch. These preferences will be
              used to determine the branch's behavior.
            </p>
            <div className="flex flex-col gap-4">
              <div className="grid grid-cols-2 items-center">
                <label className="block text-gray-700">Menu Preference</label>
                <select
                  className="rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                  onChange={(e) =>
                    setBranchPreference({
                      menu_preference: e.target.value,
                    })
                  }
                  value={branchPreference.menu_preference}
                >
                  <option value="Physical Products">Physical Products</option>
                  <option value="Services">Services</option>
                </select>
              </div>
              <div className="grid grid-cols-2 items-center">
                <label className="block text-gray-700">
                  Payment Preference
                </label>
                <select
                  className="rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                  onChange={(e) =>
                    setPaymentPreference({
                      payment_preference: e.target.value,
                    })
                  }
                  value={paymentPreference.payment_preference}
                >
                  <option value="PrePaid">PrePaid (Pay Online)</option>
                  <option value="PostPaid">PostPaid (Pay At Counter)</option>
                  <option value="Customer's Choice">
                    Customer's Choice (Pay At Counter or Online)
                  </option>
                </select>
              </div>
              <div className="grid grid-cols-2 items-center">
                <label className="block text-gray-700">
                  Appointment Buffer Time (in minutes)
                </label>
                <input
                  type="number"
                  value={appointmentBufferTime}
                  onChange={(e) =>
                    setAppointmentBufferTime(parseInt(e.target.value))
                  }
                  className="rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="0"
                />
              </div>
              <div className="grid grid-cols-2 items-center">
                <label className="block text-gray-700">Tax Rate (in %)</label>
                <input
                  type="number"
                  value={taxRate}
                  onChange={(e) => setTaxRate(parseFloat(e.target.value))}
                  className="rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="0"
                />
              </div>
              <div className="grid grid-cols-2 items-center">
                <label className="block text-gray-700">
                  Tip Options (in %)
                </label>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  <input
                    type="number"
                    value={tipOptions.tip_1}
                    onChange={(e) =>
                      setTipOptions({
                        ...tipOptions,
                        tip_1: parseInt(e.target.value),
                      })
                    }
                    className="rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                    placeholder="1st Option"
                  />
                  <input
                    type="number"
                    value={tipOptions.tip_2}
                    onChange={(e) =>
                      setTipOptions({
                        ...tipOptions,
                        tip_2: parseInt(e.target.value),
                      })
                    }
                    className="rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                    placeholder="2nd Option"
                  />
                  <input
                    type="number"
                    value={tipOptions.tip_3}
                    onChange={(e) =>
                      setTipOptions({
                        ...tipOptions,
                        tip_3: parseInt(e.target.value),
                      })
                    }
                    className="rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                    placeholder="3rd Option"
                  />
                </div>
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <button
                onClick={handleSaveBranchPreference}
                className="inline-flex items-center rounded-md border border-transparent bg-red-500 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Save Changes
              </button>
              <button
                onClick={handleCancelBranchPreference}
                className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Cancel
              </button>
            </div>
          </div>

          {/* Feature Section */}
          <FeatureManagement
            featureState={featureState}
            handleChange={handleChange}
          />

          {/* Branch Delivery Cost Section */}
          {featureState?.delivery_feature && (
            <div className="my-4 rounded-lg bg-white p-4 shadow">
              <h3 className="mb-2 text-lg font-semibold">
                Branch Delivery Cost
              </h3>
              <p className="pb-4">
                You can set a delivery cost for each branch. This cost will be
                added to the total amount of the order.
              </p>
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                <div className="grid grid-cols-2 items-center lg:grid-cols-4">
                  <label className="block text-gray-700">Delivery Cost:</label>
                  <input
                    type="number"
                    value={selectedBranch.delivery_cost}
                    className="focus:shadow-outline w-full appearance-none rounded border px-3 py-2 leading-tight text-gray-700 shadow focus:outline-none lg:col-span-2"
                    placeholder="0.00"
                    onChange={(e) =>
                      setSelectedBranch((p) => {
                        return {
                          ...p,
                          delivery_cost: parseFloat(e.target.value),
                        };
                      })
                    }
                  />
                </div>
                <div className="grid grid-cols-2 items-center lg:grid-cols-4">
                  <label className="block text-gray-700">Base Distance:</label>
                  <input
                    type="number"
                    value={selectedBranch.base_distance}
                    className="focus:shadow-outline w-full appearance-none rounded border px-3 py-2 leading-tight text-gray-700 shadow focus:outline-none lg:col-span-2"
                    placeholder="0.00"
                    onChange={(e) =>
                      setSelectedBranch((p) => {
                        return {
                          ...p,
                          base_distance: parseFloat(e.target.value),
                        };
                      })
                    }
                  />
                </div>
                <div className="grid grid-cols-2 items-center lg:grid-cols-4">
                  <label className="block text-gray-700">
                    Delivery Cost Per Additional Mile:
                  </label>
                  <input
                    type="number"
                    value={selectedBranch.delivery_cost_per_additional_mile}
                    className="focus:shadow-outline w-full appearance-none rounded border px-3 py-2 leading-tight text-gray-700 shadow focus:outline-none lg:col-span-2"
                    placeholder="0.00"
                    onChange={(e) =>
                      setSelectedBranch((p) => {
                        return {
                          ...p,
                          delivery_cost_per_additional_mile: parseFloat(
                            e.target.value,
                          ),
                        };
                      })
                    }
                  />
                </div>
                <div className="grid grid-cols-2 items-center lg:grid-cols-4">
                  <label className="block text-gray-700">Max Distance</label>
                  <input
                    type="number"
                    value={selectedBranch.max_distance}
                    className="focus:shadow-outline w-full appearance-none rounded border px-3 py-2 leading-tight text-gray-700 shadow focus:outline-none lg:col-span-2"
                    placeholder="0.00"
                    onChange={(e) =>
                      setSelectedBranch((p) => {
                        return {
                          ...p,
                          max_distance: parseFloat(e.target.value),
                        };
                      })
                    }
                  />
                </div>
              </div>
              <div>
                <button
                  onClick={() =>
                    handleChange(
                      "delivery_feature",
                      featureState.delivery_feature,
                    )
                  }
                  className="mt-4 inline-flex items-center rounded-md border border-transparent bg-red-500 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  Save
                </button>
              </div>
            </div>
          )}

          {/* Update Promotion Text Section */}
          <div className="my-4 rounded-lg bg-white p-4 shadow">
            <div className="col-span-2">
              <label
                htmlFor="promotionText"
                className="mb-2 block text-lg font-medium"
              >
                Promotion Text
              </label>
              <input
                id="promotionText"
                type="text"
                value={promotionText}
                onChange={handlePromotionTextChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
            </div>
            <div className="col-span-2 mt-4">
              <button
                onClick={updatePromotionText}
                className="inline-flex items-center rounded-md border border-transparent bg-red-500 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Save Promotion Text
              </button>
            </div>
          </div>

          {/* Update Logo Section */}
          <div className="my-4 rounded-lg bg-white p-4 shadow">
            <h3 className="mb-2 text-lg font-semibold">Update Branch Logo</h3>
            <div
              className={`block w-full border-2 ${
                isLogoUploaded ? "border-solid" : "border-dotted"
              } relative flex h-48 cursor-pointer items-center justify-center rounded-md border-gray-300 shadow-sm hover:border-gray-500`}
            >
              <input
                id="avatarUpload"
                type="file"
                className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                onChange={handleLogoUpload}
                accept="image/*"
              />
              {logoPreviewUrl ? (
                <>
                  <img
                    src={logoPreviewUrl}
                    alt="Avatar Preview"
                    className="max-h-full max-w-full p-2"
                  />
                  <button
                    onClick={handleRemoveLogo}
                    className="absolute right-0 top-0 m-2 rounded-full bg-red-500 p-1 text-white"
                    style={{ width: "30px", height: "30px" }}
                  >
                    <FontAwesomeIcon icon={faTrashAlt} />
                  </button>
                </>
              ) : (
                <div className="text-center">
                  <FontAwesomeIcon
                    icon={faImage}
                    size="2x"
                    className="mb-2 text-gray-400"
                  />
                  <p className="mb-2 text-gray-600">
                    Click or drag and drop an image to upload
                  </p>
                </div>
              )}
            </div>
            <button
              onClick={handleUpdateLogo}
              className="mt-4 inline-flex items-center rounded-md border border-transparent bg-red-500 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              disabled={!logo}
            >
              Update Branch Logo
            </button>
          </div>

          {/* Branch Operating Hours Section */}
          <div className="my-4 rounded-lg bg-white p-4 shadow">
            <BranchOperatingHoursInput
              operatingHours={operatingHours}
              setOperatingHours={setOperatingHours}
              branchId={selectedBranch?.id || 0}
            />
          </div>

          {/* Auto Gratuity Section */}
          <div className="my-4 rounded-lg bg-white p-4 shadow">
            <h3 className="mb-2 text-lg font-semibold">
              Auto Gratuity Settings
            </h3>
            <p className="mb-4 text-sm text-gray-600">
              Configure automatic gratuity settings for large parties
            </p>
            <div className="grid gap-4 sm:grid-cols-1 md:grid-cols-2">
              <div className="grid grid-cols-2 items-center">
                <label className="block text-gray-700">Guest Threshold:</label>
                <input
                  type="number"
                  value={selectedBranch.auto_gratuity_guest_threshold || 0}
                  className="focus:shadow-outline w-full appearance-none rounded border px-3 py-2 leading-tight text-gray-700 shadow focus:outline-none"
                  placeholder="0"
                  onChange={(e) =>
                    setSelectedBranch((prev) => ({
                      ...prev,
                      auto_gratuity_guest_threshold:
                        parseInt(e.target.value) || 0,
                    }))
                  }
                />
              </div>
              <div className="grid grid-cols-2 items-center">
                <label className="block text-gray-700">
                  Gratuity Percentage:
                </label>
                <input
                  type="number"
                  value={selectedBranch.auto_gratuity_percentage || 0}
                  className="focus:shadow-outline w-full appearance-none rounded border px-3 py-2 leading-tight text-gray-700 shadow focus:outline-none"
                  placeholder="0"
                  onChange={(e) =>
                    setSelectedBranch((prev) => ({
                      ...prev,
                      auto_gratuity_percentage: parseInt(e.target.value) || 0,
                    }))
                  }
                />
              </div>
            </div>
            <div>
              <button
                onClick={() => handleChange("auto_gratuity", true)}
                className="mt-4 inline-flex items-center rounded-md border border-transparent bg-red-500 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Save Auto Gratuity Settings
              </button>
            </div>
          </div>

          {/* Branch Control */}
          <div className="my-4 rounded-lg bg-white p-4 shadow">
            <h1 className="mb-2 text-lg font-semibold">Branch Control</h1>
            {showWarningPopup && (
              <WarningPopup
                title={warningPopUp.title}
                description={warningPopUp.description}
                onCancel={warningPopUp.onCancel}
                onConfirm={warningPopUp.onConfirm}
              />
            )}
            <div className="flex gap-2">
              {selectedBranch.is_active ? (
                <button
                  className="rounded-md bg-red-500 px-4 py-2 text-white"
                  onClick={() =>
                    showWarningPopupHandler({
                      title: "Deactivate Branch",
                      description: t(
                        "back.management.dashboard.branch.deactivate_warning",
                      ),
                      onCancel: () => {
                        console.log("onCancel");
                        setShowWarningPopup(false);
                      },
                      onConfirm: () => {
                        handleChange("is_active", false);
                        setShowWarningPopup(false);
                      },
                    })
                  }
                >
                  Deactivate Branch
                </button>
              ) : (
                <button
                  className="rounded-md bg-red-500 px-4 py-2 text-white"
                  onClick={() =>
                    showWarningPopupHandler({
                      title: "Reactivate Branch",
                      description: t(
                        "back.management.dashboard.branch.reactivate_warning",
                      ),
                      onCancel: () => {
                        console.log("onCancel");
                        setShowWarningPopup(false);
                      },
                      onConfirm: () => {
                        handleChange("is_active", true);
                        setShowWarningPopup(false);
                      },
                    })
                  }
                >
                  Reactivate Branch
                </button>
              )}
              <button
                className="rounded-md border border-red-500 px-4 py-2 text-red-500"
                onClick={() =>
                  showWarningPopupHandler({
                    title: "Delete Branch",
                    description: t(
                      "back.management.dashboard.branch.delete_warning",
                    ),
                    onCancel: () => {
                      console.log("onCancel");
                      setShowWarningPopup(false);
                    },
                    onConfirm: () => {
                      handleDeleteBranch();
                      setShowWarningPopup(false);
                    },
                  })
                }
              >
                Delete Branch
              </button>
            </div>
          </div>
        </div>
      )}
    </BranchRequiredWrapper>
  );
};

export default BusinessInformation;
